import { useEffect, useState } from "react";
import api from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { PdfViewer } from "./PdfViewer";

/**
 * Inline preview of a document (PDF/image) for the admin.
 * Fetches the file via axios so the JWT auth header is included, then renders
 * via blob URL inside an <iframe> (PDFs) or <img> (images).
 */
export const DocumentPreviewDialog = ({ open, document: doc, onClose, acknowledged = false }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !doc?.id) return;
        let cancelled = false;
        let url = null;
        const fetchFile = async () => {
            setLoading(true);
            setError(null);
            try {
                const resp = await api.get(`/documents/${doc.id}/download`, {
                    params: acknowledged ? { inline: true, acknowledged: true } : { inline: true },
                    responseType: "blob",
                });
                if (cancelled) return;
                url = URL.createObjectURL(resp.data);
                setBlobUrl(url);
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.detail || "Errore caricamento anteprima");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchFile();
        return () => {
            cancelled = true;
            if (url) URL.revokeObjectURL(url);
            setBlobUrl(null);
        };
    }, [open, doc?.id, acknowledged]);

    const fnameLower = (doc?.filename || "").toLowerCase();
    const isImage = (doc?.content_type || "").startsWith("image/")
        || /\.(jpe?g|png|gif|webp|heic)$/.test(fnameLower);
    const isPdf = (doc?.content_type || "").includes("pdf") || fnameLower.endsWith(".pdf");

    const handleDownload = async () => {
        try {
            const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.filename || "documento";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Errore download");
        }
    };

    return (
        <Dialog open={open} modal={false} onOpenChange={(o) => { if (!o) onClose(); }}>
            {open && (
                <div
                    className="fixed inset-0 z-40 bg-black/70"
                    aria-hidden="true"
                    onClick={onClose}
                    data-testid="preview-backdrop"
                />
            )}
            <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col" data-testid="document-preview-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <FileText className="h-5 w-5 text-[#376DB3]" />
                        Anteprima documento
                    </DialogTitle>
                    <DialogDescription className="truncate font-mono text-xs">
                        {doc?.filename}
                    </DialogDescription>
                </DialogHeader>

                <div className={`relative overflow-hidden rounded-md border border-slate-200 bg-slate-50 ${(!loading && !error && blobUrl && isPdf) ? "h-[70vh]" : "flex min-h-[30vh] items-center justify-center"}`}>
                    {loading && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
                    {error && (
                        <div className="p-6 text-center text-sm text-red-600">{error}</div>
                    )}
                    {!loading && !error && blobUrl && isPdf && (
                        <div className="absolute inset-0">
                            <PdfViewer fileUrl={blobUrl} />
                        </div>
                    )}
                    {!loading && !error && blobUrl && isImage && (
                        <img
                            src={blobUrl}
                            alt={doc?.filename}
                            className="max-h-[70vh] max-w-full object-contain"
                            data-testid="preview-img"
                        />
                    )}
                    {!loading && !error && blobUrl && !isPdf && !isImage && (
                        <div className="p-6 text-center text-sm text-slate-500">
                            Anteprima non disponibile per questo formato. Scarica il file per visualizzarlo.
                        </div>
                    )}
                </div>

                <div className="flex flex-shrink-0 justify-end gap-2">
                    <Button variant="outline" onClick={handleDownload} className="gap-2" data-testid="preview-download-btn">
                        <Download className="h-4 w-4" />
                        Scarica
                    </Button>
                    <Button onClick={onClose} className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90">Chiudi</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
